import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import Card from '../components/common/Card'
import Spinner from '../components/common/Spinner'
import Confetti from '../components/player/Confetti'
import LiveWave from '../components/player/LiveWave'
import RepeatPanel from '../components/player/RepeatPanel'
import ReviewCompare from '../components/player/ReviewCompare'
import ScoreSummary from '../components/player/ScoreSummary'
import TappableSentence from '../components/player/TappableSentence'
import { LockedTranscript } from '../components/player/TranscriptLock'
import WordFeedback from '../components/player/WordFeedback'
import ArcadeLockBanner from '../components/learning/ArcadeLockBanner'
import FeedConsentGate from '../components/social/FeedConsentGate'
import ShareToFeed from '../components/social/ShareToFeed'
import { useAutoPost, useRepeatRule } from '../hooks/useFeatures'
import { useMediaPrefetch } from '../hooks/useMediaPrefetch'
import { useMicPermission } from '../hooks/useMicPermission'
import { micView } from '../lib/micPermission'
import { PLAYBACK_SPEEDS, useShadow } from '../hooks/useShadow'
import { useSendsDone, useTranscriptLock } from '../hooks/useTranscriptLock'
import { clock } from '../lib/clock'
import { recordedChip } from '../lib/practice'
import { classifyFailure, errorMessage } from '../services/api'
import { useAuthStore } from '../store/authStore'
import { useLearningStore } from '../store/learningStore'
import { usePrefsStore } from '../store/prefsStore'
import { showToast } from '../store/toastStore'

function MicGlyph() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-brand-600" aria-hidden="true">
      <rect x="9" y="2.5" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
      <path d="M12 17.5V21" />
    </svg>
  )
}

/** Design page 5 (`watch`); mirrors the Android app's `SubtitleMode` order and labels exactly. */
function subtitleModes(t) {
  return [
    { key: 'en', label: t('subtitleModes.en') },
    { key: 'both', label: t('subtitleModes.both') },
    { key: 'off', label: t('subtitleModes.off') },
  ]
}

/** Shared by both the video and the reference-audio card — same speeds, same shadow. */
function SpeedControl({ speed, onSelect }) {
  const { t } = useTranslation('lesson')
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2" role="group" aria-label={t('playbackSpeed')}>
      {PLAYBACK_SPEEDS.map((value) => (
        <button
          key={value}
          type="button"
          aria-pressed={speed === value}
          onClick={() => onSelect(value)}
          className={`tnum rounded-lg px-3 py-1.5 text-sm transition-colors ${
            speed === value
              ? 'bg-brand-600 text-white'
              : 'bg-white text-brand-700 ring-1 ring-brand-200 hover:bg-brand-50 dark:bg-slate-800'
          }`}
        >
          {value}×
        </button>
      ))}
    </div>
  )
}

/**
 * Plan B's answer (V9-w): the take reached the server and was accepted, but
 * Azure has not scored it — it was unreachable, or the admin has
 * `planB.enabled` on. Information, not a failure. There is nothing to
 * retry: the attempt row exists, the requeue job or an admin scores it, and
 * the learner gets the usual result notification when it lands. So this
 * takes the brand tint and a check rather than the rose failure card, and
 * offers no «ارسال دوباره» — a second send would only ask after the row the
 * first one already made.
 *
 * Exported for the page's smoke test, which cannot drive the store as far
 * as a 202 under `renderToString` (see Lesson.test.jsx) and renders this
 * directly with the receipt as a prop.
 */
export function QueuedNotice() {
  const { t } = useTranslation('lesson')
  return (
    <Card>
      <div className="flex items-center gap-3 rounded-xl bg-brand-50 p-4">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-brand-600">
          <path d="M20 6L9 17l-5-5" />
        </svg>
        <p className="text-sm font-semibold text-brand-800">{t('queued.title')}</p>
      </div>
    </Card>
  )
}

/** Phases past the repetitions (R13): the last take is in the review, on its way, or answered. */
const SENT_OR_REVIEWED = ['reviewing', 'submitting', 'result', 'failed', 'queued']

export default function Lesson() {
  const { t } = useTranslation('lesson')
  const { lessonId } = useParams()
  // The home card names the first untried sentence and asks for it here;
  // without the parameter the lesson keeps its usual pick.
  const [search] = useSearchParams()
  const preferredIndex = search.has('sentence') ? Number(search.get('sentence')) : null

  const lesson = useLearningStore((s) => s.lesson)
  const loading = useLearningStore((s) => s.loading)
  const error = useLearningStore((s) => s.error)
  const loadLesson = useLearningStore((s) => s.loadLesson)
  const reset = useLearningStore((s) => s.reset)

  const defaultSpeed = usePrefsStore((s) => s.defaultSpeed)
  // R13: how many repetitions, how close each must be, and whether the server
  // posts the result itself — all from `me_profile.features`, off by default.
  const repeat = useRepeatRule()
  const autoPost = useAutoPost()
  const shadow = useShadow(lesson, preferredIndex, defaultSpeed, { repeat })
  const { audio, repetition } = shadow
  // Owner, 2026-10-18: the reference opened late. The file (and a video's
  // replay track) is fetched once, whole, into the worker's cache; the player
  // preloads once that is done, so it comes over the network a single time.
  const mediaWarm = useMediaPrefetch([shadow.referenceMedia.src, shadow.replayTrackSrc])
  // The repetitions are the way to the review: their panel shows until the last one counted.
  const repeating = repetition.on && !SENT_OR_REVIEWED.includes(shadow.phase)
  // The first visit fetches the recognizer's model; the microphone waits for it (or for its failure).
  // A single take types its live text too (owner, 2026-10-14) — until it is in the review.
  const singleLive = !repetition.on && !SENT_OR_REVIEWED.includes(shadow.phase)
  const modelLoading = (repeating || singleLive) && shadow.liveText.status === 'loading'
  // B9's contract is already live (lessons/detail carries videoUrl); this
  // gates itself on real content, not a flag — no lesson has one yet.
  const [subtitles, setSubtitles] = useState('both')
  // The browser's microphone permission (owner, 2026-10-14: asked for once):
  // a site blocked in the browser shows as such before any tap.
  const mic = useMicPermission()
  // The transcript lock (owner rule): the sentence text stays closed until
  // three failed tries of this lesson; re-read after every send.
  const sendsDone = useSendsDone(shadow.analyzing)
  const lock = useTranscriptLock(lesson, sendsDone, { wrongReps: repetition.wrong })

  useEffect(() => {
    void loadLesson(lessonId).catch(() => {})
    return reset
  }, [lessonId, loadLesson, reset])

  // A scored attempt moved the learner's XP: the profile the home and profile
  // pages show is read again now, not at the next reload.
  const scored = shadow.phase === 'result'
  useEffect(() => {
    if (scored) void useAuthStore.getState().refreshProfile({ force: true })
  }, [scored])

  // No sentences on the page (owner, 2026-10-14: «جملات اضافه و توضیحی
  // نمیخوام توی صفحه»): a repetition that did not count, a live text the
  // device cannot run and a send that failed are each a short toast.
  const lastRep = repetition.lastRep
  useEffect(() => {
    if (!lastRep || lastRep.counted) return
    showToast(t(lastRep.tooShort ? 'repeat.tooShort' : 'repeat.wrongToast'))
  }, [lastRep, t])
  const liveStatus = shadow.liveText.status
  useEffect(() => {
    if (liveStatus === 'unavailable') showToast(t('repeat.unavailableToast'))
  }, [liveStatus, t])
  const sendFailure = lesson && error && !shadow.analyzing ? classifyFailure(error) : null
  useEffect(() => {
    if (sendFailure) showToast(t(`failure.${sendFailure}.title`))
  }, [sendFailure, error, t])

  // Owner decision 2026-10-15 («بله، خودکار»): the fifth correct repetition goes
  // to Azure on its own — no review, no button — once per take. A take kept
  // from an earlier visit (no verdict on it), a cancelled send and a failed one
  // stay on the review with «ارسال برای تحلیل», as before.
  const autoSend = repetition.on && shadow.phase === 'reviewing' && repetition.lastRep?.counted === true && Boolean(audio.blob)
  const [autoSentFor, setAutoSentFor] = useState(null)
  const submitTake = shadow.submit
  useEffect(() => {
    if (!autoSend || autoSentFor === audio.previewUrl) return
    setAutoSentFor(audio.previewUrl)
    void submitTake({ transcriptOpenedLocally: lock.openedLocally }).catch(() => {})
  }, [autoSend, autoSentFor, audio.previewUrl, submitTake, lock.openedLocally])
  // Until it starts, the send's own panel already shows (no flash of the review).
  const autoPending = autoSend && autoSentFor !== audio.previewUrl
  // Once it has its answer the take is done: no review, no dead «ارسال» under
  // the result — the card goes back to a new practice («۱/۵», the microphone).
  const autoDone = repetition.on && autoSentFor !== null && autoSentFor === audio.previewUrl && (shadow.phase === 'result' || shadow.phase === 'queued')
  const discardTake = shadow.discardRecording
  useEffect(() => {
    if (autoDone) discardTake()
  }, [autoDone, discardTake])

  // Before the load has even started — the effect runs after the first paint —
  // there is nothing to say yet; «درس پیدا نشد.» is for a load that failed.
  if (!lesson && (loading || !error)) return <Spinner label={t('loading')} />
  if (!lesson) {
    return (
      <Card>
        <p className="text-sm text-rose-600 dark:text-rose-400">{errorMessage(error, t('notFound'))}</p>
      </Card>
    )
  }

  const { sentence } = shadow
  if (!sentence) {
    return (
      <Card>
        <p className="text-sm text-rose-600 dark:text-rose-400">{t('noSentenceYet')}</p>
      </Card>
    )
  }

  // Where the microphone button goes: the button, or one red line when the
  // browser has blocked the site (known before any tap — also with the shared
  // stream still held, whose tracks the browser ends) or there is no device.
  const micCard = audio.state === 'idle' || audio.state === 'ready' ? micView({ permission: mic.permission, errorCode: audio.errorCode }) : 'ready'
  const hasVideo = Boolean(lesson.videoUrl)
  const showEnglish = !hasVideo || subtitles !== 'off'
  const showFarsi = !hasVideo || subtitles === 'both'

  return (
    <div className="space-y-5">
      {/* R13: the accepted 5th repetition — confetti over everything, with the `rep_final_ok` cue. */}
      <Confetti burst={shadow.celebration} />

      {/* Q13: a direct link to a lesson the Arcade path still keeps shut
          opens it and says so — it never redirects. Silent otherwise. */}
      <ArcadeLockBanner lessonId={lessonId} />

      <Card>
        {/* Lessons authored since 2026-10-13 have no Persian title (teaching is
            monolingual): the English one is then the heading, not a subtitle. */}
        {lesson.titleFa ? (
          <>
            <h1 className="text-lg font-bold text-slate-800 dark:text-slate-100">{lesson.titleFa}</h1>
            <p className="ltr text-sm text-slate-400 dark:text-slate-500">{lesson.titleEn}</p>
          </>
        ) : (
          <h1 className="ltr text-lg font-bold text-slate-800 dark:text-slate-100">{lesson.titleEn}</h1>
        )}

        {/* The script is Latin text inside an RTL page, so it gets its own
            direction rather than inheriting the document's — and its own
            typeface: Newsreader, not the Persian UI face. With a video this
            stays plain text, hidden by the video's own subtitle mode
            (SUBTITLE_MODES below) exactly as before — that feature is
            untouched. Without a video there is no subtitle toggle to hide it
            with, so instead of always stacking the whole-sentence Farsi
            translation underneath, each English word is its own tap target
            for an admin-authored note (TappableSentence): owner request, no
            go-live-plan item. translationFa itself is unchanged — still
            authored, still stored — only this one view no longer renders it
            stacked. */}
        {/* V2-w: the tappable sentence in the video lesson too — the subtitle
            mode still decides whether the English line shows at all.
            Transcript lock: none of it — the text, its translation, the word
            notes — before three failed tries of the lesson. */}
        {!lock.unlocked ? (
          <div className="mt-3">
            <LockedTranscript lock={lock} />
          </div>
        ) : hasVideo ? (
          <>
            {showEnglish && (
              <TappableSentence textEn={sentence.textEn} wordNotes={sentence.wordNotes} words={sentence.words} />
            )}

            {showFarsi && sentence.translationFa && (
              <p className="mt-3 text-sm leading-relaxed text-slate-500 dark:text-slate-400">
                {sentence.translationFa}
              </p>
            )}
          </>
        ) : (
          <TappableSentence textEn={sentence.textEn} wordNotes={sentence.wordNotes} words={sentence.words} />
        )}
      </Card>

      {hasVideo ? (
        <Card>
          <video
            ref={shadow.bindAudio}
            src={shadow.referenceMedia.src}
            onLoadedMetadata={shadow.onLoadedMetadata}
            onPlay={shadow.onPlay}
            onPause={shadow.onPause}
            onEnded={shadow.onEnded}
            onTimeUpdate={shadow.onTimeUpdate}
            preload={mediaWarm ? 'auto' : 'metadata'}
            className="w-full rounded-xl bg-black"
            controls
          />

          {/* The subtitle modes choose how the text shows — nothing to choose while it is closed. */}
          {lock.unlocked && (
          <div className="mt-4 flex items-center gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800" role="radiogroup" aria-label={t('subtitleModeAriaLabel')}>
            {subtitleModes(t).map(({ key, label }) => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={subtitles === key}
                onClick={() => setSubtitles(key)}
                className={`flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${
                  subtitles === key ? 'bg-white text-brand-700 shadow-sm dark:bg-slate-900' : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          )}

          <SpeedControl speed={shadow.speed} onSelect={shadow.setSpeed} />
        </Card>
      ) : (
        <Card>
          <audio
            ref={shadow.bindAudio}
            src={shadow.referenceMedia.src}
            onLoadedMetadata={shadow.onLoadedMetadata}
            onPlay={shadow.onPlay}
            onPause={shadow.onPause}
            onEnded={shadow.onEnded}
            onTimeUpdate={shadow.onTimeUpdate}
            preload={mediaWarm ? 'auto' : 'metadata'}
            className="w-full"
            controls
          />

          <SpeedControl speed={shadow.speed} onSelect={shadow.setSpeed} />
        </Card>
      )}

      <Card>
        {/* R13: while every result is posted, the practice opens only after the learner agreed to it, once. */}
        <FeedConsentGate enabled={autoPost}>
        {(repeating || singleLive) && <RepeatPanel repetition={repetition} liveText={shadow.liveText} recording={audio.isRecording} textOpen={lock.unlocked} />}
        {micCard === 'denied' ? (
          // Blocked — the browser will not show its own prompt again. There is
          // no OS-settings deep link on the web the way Android has one.
          <p data-mic-card="denied" className="text-center text-sm font-medium text-rose-700 dark:text-rose-300">{t('mic.deniedTitle')}</p>
        ) : micCard === 'unavailable' ? (
          <p data-mic-card="unavailable" className="text-center text-sm font-medium text-rose-700 dark:text-rose-300">{t('mic.unavailableTitle')}</p>
        ) : audio.isRecording ? (
          // Recording (design page 6): the pulsing dot and the timer, the live strip
          // measured from the microphone, the red stop button.
          <div className="flex flex-col items-center gap-3.5">
            <span className="flex items-center gap-2" role="timer" aria-live="off">
              <span aria-hidden="true" className="size-[9px] animate-pulse rounded-full bg-bad" />
              <span dir="ltr" className="tnum text-sm font-semibold text-bad">
                {clock(audio.seconds)}
              </span>
            </span>
            <LiveWave levels={audio.levels} />
            <button
              type="button"
              onClick={shadow.stopRecording}
              aria-label={t('mic.stopAriaLabel')}
              className="grid size-[74px] place-items-center rounded-full bg-bad shadow-[0_8px_22px_rgba(210,82,63,0.32)] transition hover:brightness-95"
            >
              <span aria-hidden="true" className="block size-6 rounded-md bg-white" />
            </button>
          </div>
        ) : repetition.checking ? null : audio.state === 'idle' || !audio.hasRecording || repeating ? (
          // Idle: the white microphone button, nothing under it — the first
          // tap is also the browser's permission prompt. While the
          // recognizer's model downloads (first visit) it waits, spinning.
          // A repetition that was not the last never reaches the review.
          <div className="flex flex-col items-center">
            <button
              type="button"
              onClick={shadow.startRecording}
              disabled={modelLoading}
              aria-label={t('mic.startAriaLabel')}
              aria-busy={audio.state === 'requesting' || modelLoading || undefined}
              className="grid size-[74px] place-items-center rounded-full border-2 border-line bg-surface shadow-sm transition hover:bg-surface-2 disabled:opacity-50"
            >
              {modelLoading ? (
                <span aria-hidden="true" className="block size-7 animate-spin rounded-full border-[3px] border-line border-t-brand-600" />
              ) : (
                <MicGlyph />
              )}
            </button>
          </div>
        ) : (
          // The review (design page 7): reference and take side by side, the two
          // lengths, then re-record or send; the sending panel while it waits.
          <ReviewCompare
            sentence={sentence}
            reference={{
              seconds: sentence.referenceAudioSeconds ?? 0,
              playing: shadow.playing,
              position: shadow.position,
              onToggle: shadow.toggleReference,
            }}
            take={{ url: audio.previewUrl, seconds: audio.recordedSeconds || audio.seconds, samples: audio.samples }}
            chip={recordedChip(audio.recordedSeconds || audio.seconds)}
            analyzing={shadow.analyzing || autoPending}
            sendPhase={shadow.sendPhase}
            onSubmit={() => {
              // R1: a text the device opened by three wrong repetitions costs the same XP share as the server's lock.
              void shadow.submit({ transcriptOpenedLocally: lock.openedLocally }).catch(() => {})
            }}
            onCancel={shadow.cancelSubmit}
            onDiscard={shadow.discardRecording}
            onPauseReference={shadow.pauseReference}
          />
        )}
        </FeedConsentGate>
      </Card>

      {/* Plan B (V9-w): a submission has three outcomes, not two. A 202
          means the server took the recording and will score it later —
          drawn here as information, above the (absent) result cards. */}
      {shadow.queued && <QueuedNotice />}

      {/* `attempts_submit` answers a score or a queue receipt, never both:
          `result` is the `AttemptResultDto`, and the store keeps a queued
          answer out of it (store/learningStore.js) precisely so these three
          cards — the bands, the words, the share button — only ever draw a
          take that has actually been judged. */}
      {shadow.result && (
        <>
          <Card title={t('resultCardTitle')}>
            <ScoreSummary assessment={shadow.result.assessment} xpAwarded={shadow.result.xpAwarded} />
          </Card>
          <Card title={t('wordFeedbackCardTitle')}>
            <WordFeedback words={shadow.result.assessment?.words ?? []} />
          </Card>
          {/* Share-to-feed (design page 8; P-core-4): the server asks for consent first when it must.
              Owner, 2026-10-14: while the server posts every scored take itself there is no manual share at all. */}
          {!autoPost && <ShareToFeed attemptId={shadow.result.attemptId} />}
        </>
      )}
    </div>
  )
}
